import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { requestTicket } from '../lib/sso';
import { BACKEND_URL } from '../lib/backend';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Guest mode state (share link)
  const [isGuest, setIsGuest] = useState(false);
  const [guestSharedToken, setGuestSharedToken] = useState(null);
  const [guestSharedFile, setGuestSharedFile] = useState(null);
  const [shareExpired, setShareExpired] = useState(false);

  const API = `${BACKEND_URL}/api`;

  // Вход аккаунтом гипнозио: тикет одноразовый и подписан общим секретом,
  // бэкенд проверяет подпись и отдаёт обычную сессию гипновейва — дальше всё
  // работает как при входе по паролю. Объявлено до эффекта ниже: тихий вход
  // при загрузке страницы зовёт эту же функцию.
  const loginWithTicket = async (ticket) => {
    try {
      const response = await axios.post(`${API}/auth/sso`, { ticket });
      const { access_token, user: userData } = response.data;

      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      setIsGuest(false);
      setGuestSharedToken(null);
      setGuestSharedFile(null);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Не удалось войти через Gipnozio',
      };
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareParam = params.get('share');

    if (token) {
      // Logged-in user — verify token
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(response => {
          setUser(response.data);
          setLoading(false);
        })
        .catch(() => {
          logout();
          setLoading(false);
        });
    } else if (shareParam) {
      // Guest via share link — validate token
      axios.get(`${API}/share/${shareParam}`)
        .then(response => {
          if (response.data.expired) {
            setShareExpired(true);
          } else {
            setIsGuest(true);
            setGuestSharedToken(shareParam);
            setGuestSharedFile(response.data.file);
          }
          setLoading(false);
        })
        .catch(() => {
          setShareExpired(true);
          setLoading(false);
        });
    } else {
      // Своей сессии нет. Прежде чем показывать экран входа — тихо спросим
      // гипнозио: может, человек там уже вошёл. Живя на поддомене, мы получаем
      // ответ сразу, без ухода из вкладки, и человек просто оказывается внутри.
      // На чужом домене cookie до гипнозио не доедет, requestTicket вернёт
      // null, и покажется экран входа с кнопкой — как раньше.
      let cancelled = false;
      requestTicket()
        .then((ticket) => {
          if (cancelled) return null;
          return ticket ? loginWithTicket(ticket) : null;
        })
        .catch(() => null)
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => { cancelled = true; };
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginUser = async (login, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { login, password });
      const { access_token, user: userData } = response.data;

      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      // Clear guest state after login
      setIsGuest(false);
      setGuestSharedToken(null);
      setGuestSharedFile(null);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Неверный логин или пароль'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      loginUser,
      loginWithTicket,
      logout,
      getAuthHeaders,
      isAuthenticated: !!user,
      isGuest,
      guestSharedToken,
      guestSharedFile,
      shareExpired,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
